import { useCallback } from 'react';
import { setAllDrawersClosed, setIsImageEditDrawerOpen } from '../../../context/app-context/app-context-actions';
import { useAppContext } from '../../../context/app-context';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { useUIStore } from '../../../stores/ui';

/**
 * Returns a single `openImageEditDrawer` function that routes to the right
 * state system based on the `IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED` feature
 * flag:
 *  - When the flag is on, the new `ImageEditToolbar` is opened via its
 *    zustand store action. We still dispatch `setAllDrawersClosed` so any
 *    open app-context drawers (e.g. the photo upload drawer) close — the
 *    legacy `SET_IS_IMAGE_EDIT_DRAWER_OPEN` reducer used to do this
 *    implicitly by spreading `closedDrawers`.
 *  - When the flag is off, the legacy `ImageEditDrawer` is opened via the
 *    app-context dispatch (existing behaviour).
 *
 * Call sites can use this hook without knowing which UI surface is active.
 */
export const useOpenImageEditDrawer = (): (() => void) => {
  const { appDispatch } = useAppContext();
  const { IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED } = useFeatureFlags();
  const openImageEditToolbar = useUIStore((state) => state.openImageEditToolbar);

  return useCallback(() => {
    if (IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED) {
      setAllDrawersClosed(appDispatch);
      openImageEditToolbar();
      return;
    }
    setIsImageEditDrawerOpen(appDispatch);
  }, [IS_NEW_IMAGE_EDIT_TOOLBAR_ENABLED, appDispatch, openImageEditToolbar]);
};
